// Validates submitted properties without changing Upload-Lift's fields or files.
class PortraitValidation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    clearInterval(this.timer);
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.details = this.closest('.personalized-product-details--portrait');
    if (!this.details) return;
    this.error = this.querySelector('[data-portrait-validation-error]');
    this.constraint = this.querySelector('[data-validation-input]');
    this.uploads = new window.LaPhotoUploadCompletion(this.details, signal, () => this.validate());
    if (this.constraint) this.constraint.hidden = true;
    this.constraint?.addEventListener('invalid', event => {
      event.preventDefault();
      this.showFeedback();
    }, { signal });
    this.details.addEventListener('click', event => {
      const button = event.target.closest('button[type="submit"], input[type="submit"]');
      if (button?.form?.id === this.dataset.formId && !this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showFeedback();
      }
    }, { capture: true, signal });
    this.details.addEventListener('change', () => this.validate(), { signal });
    this.details.addEventListener('input', () => this.validate(), { signal });
    document.addEventListener('submit', event => {
      if (event.target.getAttribute('id') !== this.dataset.formId) return;
      if (!this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showFeedback();
      }
    }, { capture: true, signal });
    // Upload apps can assign hidden-input.value without emitting input/change.
    // Recheck only our validation state; never observe or rewrite app internals.
    this.timer = setInterval(() => this.validate(), 300);
    this.validate();
  }

  showFeedback() {
    const feedback = this.backgroundError && !this.backgroundError.hidden ? this.backgroundError : this.error;
    if (!feedback?.hidden) feedback?.scrollIntoView({ block: 'center', behavior: 'auto' });
  }

  validate() {
    const form = document.getElementById(this.dataset.formId);
    if (!form || !this.details || !this.error || !this.constraint) return true;
    const sizeGroup = this.details.querySelector('[data-portrait-kind="size"]');
    const formatSelector = this.details.querySelector('portrait-format-selector');
    const size = formatSelector?.size?.selectedOptions[0]?.textContent || [...this.details.querySelectorAll('variant-picker input:checked[data-option-name], variant-picker option:checked[data-option-name]')]
      .find(input => input.dataset.optionName.toLowerCase() === sizeGroup?.dataset.portraitOption?.toLowerCase())?.value || '';
    const dimensions = size.match(/(\d+)\s*["″]?\s*[x×]\s*(\d+)/i);
    const nativeOrientation = this.details.querySelector('variant-picker input[data-option-name="Orientation"]:checked')?.value;
    const digital=this.details.hasAttribute('data-inline-digital-selected');
    const onePet = digital || !/horizontal/i.test(formatSelector ? size : nativeOrientation || size);
    this.querySelector('[data-one-pet-guidance]').hidden = !onePet;
    const data = new FormData(form);
    const nameInput = this.details.querySelector('[data-pet-name]');
    const names = nameInput && !nameInput.disabled ? nameInput.value.split(',').map(name => name.trim()).filter(Boolean) : [];
    const tier = this.details.querySelector('[data-background-tier]:checked')?.dataset.backgroundTier;
    const previewOnly = (tier === 'premium' || tier === 'limited') && !form.querySelector('artwork-services');
    this.backgroundError = this.details.querySelector('[data-background-purchase-error]');
    const backgroundMessage = previewOnly ? 'Preview only — unavailable for purchase. Choose a standard background to add this portrait to your cart.' : '';
    if (this.backgroundError) {
      if (this.backgroundError.textContent !== backgroundMessage) this.backgroundError.textContent = backgroundMessage;
      this.backgroundError.hidden = !previewOnly;
    }
    let message = '';
    const excluded = !digital && dimensions && [Number(dimensions[1]), Number(dimensions[2])].sort((a,b) => a-b).join('x') === '16x20';
    if (!this.uploads.ready(data)) {
      message = 'Please upload at least one pet photo and wait for it to finish before adding this portrait to your cart.';
    } else if (excluded) {
      message = 'This size is no longer offered here. Choose an available portrait size before adding to cart.';
    } else if (onePet && names.length > 1) {
      message = digital ? 'Digital portrait includes one pet. Enter one name or choose No name.' : 'For portraits with 2–4 pets, please select any horizontal portrait size.';
    } else if (previewOnly) {
      // A paid choice must have the chargeable studio-service integration.
      message = backgroundMessage;
    }
    if (this.error.textContent !== message) this.error.textContent = message;
    this.error.hidden = !message || (previewOnly && message === backgroundMessage && Boolean(this.backgroundError));
    this.constraint.setCustomValidity(message);
    this.details.toggleAttribute('data-portrait-purchase-invalid', Boolean(message));
    for (const button of form.querySelectorAll('button[type="submit"], input[type="submit"]')) {
      if (message) {
        if (!button.dataset.portraitBlocked) button.dataset.portraitPreviousAria = button.getAttribute('aria-disabled') ?? '';
        button.setAttribute('aria-disabled', 'true'); button.dataset.portraitBlocked = 'true';
      } else if (button.dataset.portraitBlocked) {
        if (button.dataset.portraitPreviousAria) button.setAttribute('aria-disabled', button.dataset.portraitPreviousAria);
        else button.removeAttribute('aria-disabled');
        delete button.dataset.portraitBlocked; delete button.dataset.portraitPreviousAria;
      }
    }
    return !message;
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearInterval(this.timer);
    this.details?.removeAttribute('data-portrait-purchase-invalid');
  }
}
if (!customElements.get('la-portrait-validation')) customElements.define('la-portrait-validation', PortraitValidation);
